import { useState, useEffect } from 'react'
import { useForm, SubmitHandler } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { Cookies } from 'react-cookie'
import { useAuth } from '../../context/auth-context'
import { useFetchCities } from '../../hooks/useFetchIranCities'
import { useFetchProvinces } from '../../hooks/useFetchProvinces'
import { client } from '../../utils/api-client'
import { makeNumbersFarsi } from 'utils/farsi'
import { Helmet } from 'react-helmet-async'

interface IFormInput {
  customerFirstName: string
  customerLastName: string
  customerTelephone: number | string
  text: string
  postalCode: number | string
}

export const AddAddressInfoForm = () => {
  const { user } = useAuth()
  const cookies = new Cookies()
  const [show, setShow] = useState(false)
  const [provinceId, setProvinceId] = useState(1)
  const [cityId, setCityId] = useState(1)
  const { cities } = useFetchCities()
  const { provinces } = useFetchProvinces()

  const handleProvinceIdChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setProvinceId(parseInt(e.target.value))
    getProvinceCityIds(provinceId)
  }

  const handleCityIdChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setCityId(parseInt(e.target.value))
  }

  const getProvinceCityIds = (provinceId: number) => {
    return cities?.filter((city) => city.provinceId === provinceId)
  }

  let provinceCityIds = getProvinceCityIds(provinceId)
  const navigate = useNavigate()
  const {
    register,
    handleSubmit,
    setFocus,
    formState: { errors },
    setValue,
  } = useForm<IFormInput>()
  const onSubmit: SubmitHandler<IFormInput> = async (options) => {
    const token = cookies.get('@bookstory-auth-token')
    const authToken = token?.user?.authToken
    let userToken = authToken?.accessToken
    let customerId = user.id
    let data = {
      customerFirstName: options.customerFirstName,
      customerLastName: options.customerLastName,
      customerTelephone: options.customerTelephone,
      text: options.text,
      postalCode: options.postalCode,
      cityId,
    }

    const result = await client(`customers/${customerId}/addresses`, {
      userToken,
      data,
    })
    if (result) setShow(true)
  }

  useEffect(() => {
    setFocus('customerFirstName')
  }, [setFocus])

  const handleBackToSettingsClick = () => {
    navigate('/settings')
  }

  const handleGoToMainPageClick = () => {
    navigate('/')
  }

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const farsiNumber = makeNumbersFarsi(e.target.value)
    setValue('customerTelephone', farsiNumber) // Update form value
  }

  const handlePostalCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const farsiNumber = makeNumbersFarsi(e.target.value)
    setValue('postalCode', farsiNumber) // Update form value
  }

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const farsiNumber = makeNumbersFarsi(e.target.value)
    setValue('text', farsiNumber) // Update form value
  }

  return (
    <div className='max-w-lg mt-5 mx-auto flex flex-col my-5 h-auto border py-8 px-4 rounded-md' style={{ backgroundColor: 'var(--card-bg)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}>
      <Helmet>
        <title className='text-xl font-semibold font-semibold'>
          افزودن اطلاعات پستی
        </title>
        <meta
          name='description'
          content='داستان کتاب - داستان ها همیشه در دل و ذهن می مانند'
        />
      </Helmet>
      <div className={show ? 'block' : 'hidden'}>
        <h2 className='text-lg text-start font-medium '>
          اطلاعات آدرس جدید شما با موفقیت ثبت شد.
        </h2>
        <div className='flex items-center justify-between w-full gap-4 my-4'>
          <button
            onClick={handleBackToSettingsClick}
            className='w-full rounded text-white uppercase border-none mt-10 p-5 text-xl font-medium active:transition-all active:duration-[0.3s] active:opacity-80 cursor-pointer'
            style={{ backgroundColor: 'var(--accent-primary)' }}
          >
            رفتن به تنظمیات
          </button>
          <button
            onClick={handleGoToMainPageClick}
            className='w-full rounded text-white uppercase border-none mt-10 p-5 text-xl font-medium active:transition-all active:duration-[0.3s] active:opacity-80 cursor-pointer'
            style={{ backgroundColor: 'var(--accent-primary)' }}
          >
            رفتن به صفحه اصلی
          </button>
        </div>
      </div>
      <div className={!show ? 'block' : 'hidden'}>
        <button
          className='rounded border-none w-full mb-4 p-4 text-white cursor-pointer'
          style={{ backgroundColor: 'var(--accent-primary)' }}
          onClick={handleBackToSettingsClick}
        >
          بازگشت به تنظیمات
        </button>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className='flex flex-col items-start my-4'>
            <label className='text-lg text-right font-medium mb-2'>
              {' '}
              استان:{' '}
            </label>{' '}
            <select
              className='rounded border-none w-full p-2 text-white cursor-pointer'
              style={{ backgroundColor: 'var(--accent-primary)' }}
              defaultValue={provinceId}
              onChange={handleProvinceIdChange}
            >
              {provinces?.map((province) => {
                return (
                  <option
                    className='hover:bg-[#81A1C1]'
                    key={province.id}
                    value={province.id}
                  >
                    {province.title}
                  </option>
                )
              })}
            </select>
          </div>
          <div className='flex flex-col items-start my-4'>
            <label className='text-lg text-right font-medium mb-2'>شهر: </label>
            <select
              className='rounded border-none w-full p-2 text-white cursor-pointer'
              style={{ backgroundColor: 'var(--accent-primary)' }}
              defaultValue={cityId}
              onChange={handleCityIdChange}
            >
              {provinceCityIds?.map((city) => {
                return (
                  <option
                    className='cursor-pointer'
                    key={city.id}
                    value={city.id}
                  >
                    {city.title}
                  </option>
                )
              })}
            </select>
          </div>
          <h1 className='text-lg text-right font-medium mt-4 mb-2'>
            نام گیرنده:
          </h1>
          <input
            className='input-theme block outline-none w-full rounded border p-4 mb-2.5 text-base'
            type='text'
            {...register('customerFirstName', {
              required: 'first name is required',
            })}
          />
          {errors.customerFirstName && (
            <p className='text-red-600 before:inline before:content-["⚠"]'>
              {' '}
              {errors.customerFirstName.message}
            </p>
          )}

          <h1 className='text-lg text-right font-medium mt-4 mb-2'>
            نام خانوادگی گیرنده:
          </h1>

          <input
            className='input-theme block outline-none w-full rounded border p-4 mb-2.5 text-base'
            type='text'
            {...register('customerLastName', {
              required: 'last name is required',
            })}
          />
          {errors.customerLastName && (
            <p className='text-red-600 before:inline before:content-["⚠"]'>
              {' '}
              {errors.customerLastName.message}
            </p>
          )}
          <h1 className='text-lg text-right font-medium mt-4 mb-2'>
            شماره تلفن:{' '}
          </h1>

          <input
            type='text'
            className='input-theme block outline-none w-full rounded border p-4 mb-2.5 text-base'
            {...register('customerTelephone', {
              maxLength: 13,
              onChange: handlePhoneNumberChange,
            })}
          />
          {errors.customerTelephone && (
            <p className='text-[#bf1650] before:inline before:content-["⚠"]'>
              {' '}
              Phone number is not correct format
            </p>
          )}

          <h1 className='text-lg text-right font-medium mt-4 mb-2'>آدرس: </h1>

          <textarea
            className='input-theme block h-auto outline-none w-full rounded border p-4 mb-2.5 text-base'
            {...register('text', {
              onChange: handleAddressChange,
            })}
          />
          {errors.text && (
            <p className='text-[#bf1650] before:inline before:content-["⚠"]'>
              {' '}
              Address is required
            </p>
          )}

          <h1 className='text-lg text-right font-medium mt-4 mb-2'>
            کد پستی:{' '}
          </h1>

          <input
            className='input-theme block outline-none w-full rounded border p-4 mb-2.5 text-base'
            type='text'
            {...register('postalCode', {
              minLength: 10,
              maxLength: 10,
              onChange: handlePostalCodeChange,
            })}
          />
          {errors.postalCode && (
            <p className='text-[#bf1650] before:inline before:content-["⚠"]'>
              {' '}
              postal code is required
            </p>
          )}
          <input
            className='w-full rounded text-white uppercase border-none mt-10 p-5 text-xl font-medium active:transition-all active:duration-[0.3s] active:opacity-80 cursor-pointer'
            style={{ backgroundColor: 'var(--accent-primary)' }}
            type='submit'
            value='تایید'
          />
        </form>
      </div>
    </div>
  )
}
